import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useUser } from '../../contexts/UserContext';
import { setLocalStorageItem, getUserSetting, setUserSetting, getLocalStorageItem } from '../../utils/localStorage';
import { api } from '../../utils/apiClient';

/**
 * Settings component that allows users to configure app preferences
 * 
 * Main features:
 * 1. Language selection (all users)
 * 2. Theme settings (all users)
 * 3. Link to account management (logged-in users)
 * 4. Link to wallet (logged-in users)
 */
function Settings() {
  const { t, i18n } = useTranslation();
  const { themeMode, setThemeMode } = useTheme();
  const { user } = useUser();
  
  // Track user language setting
  const [userLanguage, setUserLanguage] = useState<string>(() => {
    if (user.email) {
      return getUserSetting<string>(user.email, 'language', i18n.language);
    }
    return getLocalStorageItem<string>('language', i18n.language);
  });
  
  // Update language state when user status or language changes
  useEffect(() => {
    // Update the component state from the current i18n language
    setUserLanguage(i18n.language);
    
    // Function to sync component state with i18n
    const handleLanguageChange = () => {
      setUserLanguage(i18n.language);
    };
    
    // Listen for i18n language changes
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [user.email, i18n]);

  // Effect to fetch user language from server when logged in
  useEffect(() => {
    const fetchUserLanguageFromServer = async () => {
      if (user.email && user.token) {
        try {
          const response = await api.user.getSettings(user.email);
          if (response.success && response.settings && response.settings.language) {
            const serverLanguage = response.settings.language;
            
            if (serverLanguage !== i18n.language) {
              i18n.changeLanguage(serverLanguage);
            }
          }
        } catch (error) {
          console.error('Error fetching language from server:', error);
        }
      }
    };
    
    fetchUserLanguageFromServer();
  }, [user.email, user.token, i18n]);

  /**
   * Handle theme selection change
   */
  const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMode = e.target.value as 'light' | 'dark' | 'auto';
    setThemeMode(newMode);
  };

  /**
   * Handle language selection change
   */
  const handleLanguageChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = e.target.value;
    
    // Clear i18next's internal storage to prevent conflicts
    localStorage.removeItem('i18nextLng');
    
    // Update our component state
    setUserLanguage(newLanguage);
    
    // Save based on login state
    if (user.email) {
      // Save to localStorage
      setUserSetting(user.email, 'language', newLanguage);
      
      // Save to database
      if (user.token) {
        try {
          const response = await api.user.updateSettings(user.email, { language: newLanguage });
          if (!response.success) {
            console.error('Failed to save language to server:', response.message);
          }
        } catch (error) {
          console.error('Error saving language to server:', error);
        }
      }
    } else {
      setLocalStorageItem('language', newLanguage);
    }
    
    // Apply the language change to i18n
    i18n.changeLanguage(newLanguage);
  };

  return (
    <div className="container py-5">
      <div className="row justify-content-center mb-4">
        <div className="col-12 col-md-10 col-lg-8">
          <div className="d-sm-flex align-items-center justify-content-between mb-4">
            <h1 className="h3 mb-0">{t('settings.title')}</h1>
            {user.email && (
              <div className="mt-3 mt-sm-0 text-muted small">
                <i className="bi bi-person-circle me-1"></i>
                {user.email}
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-body-tertiary py-3">
              <h2 className="h5 mb-0">
                <i className="bi bi-sliders me-2 text-primary"></i>
                {t('settings.appearance')}
              </h2>
            </div>
            <div className="card-body">
              <div className="row">
                {/* Theme Settings */}
                <div className="col-md-6 mb-4">
                  <div className="mb-3">
                    <label htmlFor="themeSelect" className="form-label fw-medium">
                      {t('settings.theme')}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-body-secondary">
                        <i className={`bi ${themeMode === 'light' ? 'bi-sun' : themeMode === 'dark' ? 'bi-moon' : 'bi-circle-half'}`}></i>
                      </span>
                      <select 
                        id="themeSelect" 
                        className="form-select" 
                        value={themeMode}
                        onChange={handleThemeChange}
                        aria-label={t('settings.theme')}
                      >
                        <option value="light">{t('settings.themeLight')}</option>
                        <option value="dark">{t('settings.themeDark')}</option>
                        <option value="auto">{t('settings.themeAuto')}</option>
                      </select>
                    </div>
                    <div className="form-text mt-2">
                      <i className="bi bi-info-circle me-1"></i>
                      {t('settings.themeDescription')}
                    </div>
                  </div>
                </div>
                
                {/* Language Settings */}
                <div className="col-md-6 mb-4">
                  <div className="mb-3">
                    <label htmlFor="languageSelect" className="form-label fw-medium">
                      {t('settings.language')}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-body-secondary">
                        <i className="bi bi-translate"></i>
                      </span>
                      <select 
                        id="languageSelect" 
                        className="form-select" 
                        value={userLanguage}
                        onChange={handleLanguageChange}
                        aria-label={t('settings.language')}
                      >
                        <option value="en">{t('common.languages.en', 'English')}</option>
                        <option value="lv">{t('common.languages.lv', 'Latvian')}</option>
                        <option value="de">{t('common.languages.de', 'German')}</option>
                        <option value="zh">{t('common.languages.zh', 'Chinese')}</option>
                      </select>
                    </div>
                    <div className="form-text mt-2">
                      <i className="bi bi-info-circle me-1"></i>
                      {t('settings.languageDescription')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* User Account Links - only show when logged in */}
          {user.email && (
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-body-tertiary py-3">
                <h2 className="h5 mb-0">
                  <i className="bi bi-person-circle me-2 text-primary"></i>
                  {t('settings.accountSettings')}
                </h2>
              </div>
              <div className="card-body">
                <div className="row g-4">
                  {/* Account Management Link */}
                  <div className="col-md-6">
                    <div className="d-flex h-100">
                      <div className="flex-shrink-0">
                        <div className="bg-body-secondary rounded p-3">
                          <i className="bi bi-person-gear fs-3 text-primary"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3 d-flex flex-column">
                        <h3 className="h6 fw-bold mb-1">{t('settings.accountManagement')}</h3>
                        <p className="text-body-secondary small mb-3 flex-grow-1">
                          {t('settings.accountManagementDescription')}
                        </p>
                        <Link to="/account" className="btn btn-outline-primary btn-sm align-self-start">
                          <i className="bi bi-gear-fill me-1"></i>
                          {t('settings.manageAccount')}
                        </Link>
                      </div>
                    </div>
                  </div>
                  
                  {/* Wallet Link */}
                  <div className="col-md-6">
                    <div className="d-flex h-100">
                      <div className="flex-shrink-0">
                        <div className="bg-body-secondary rounded p-3">
                          <i className="bi bi-wallet2 fs-3 text-primary"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3 d-flex flex-column">
                        <h3 className="h6 fw-bold mb-1">{t('settings.wallet')}</h3>
                        <p className="text-body-secondary small mb-3 flex-grow-1">
                          {t('settings.walletDescription')}
                        </p>
                        <Link to="/wallet" className="btn btn-outline-primary btn-sm align-self-start">
                          <i className="bi bi-credit-card me-1"></i>
                          {t('settings.manageWallet')}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Help text for non-logged in users */}
          {!user.email && (
            <div className="alert alert-info bg-info-subtle text-info-emphasis border-info-subtle mt-4">
              <div className="d-flex">
                <div className="flex-shrink-0">
                  <i className="bi bi-info-circle-fill fs-4"></i>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h5 className="alert-heading">{t('settings.notLoggedIn')}</h5>
                  <p className="mb-0">
                    {t('settings.loginForMoreSettings')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings; 